import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { SubscriptionModal } from './SubscriptionModal';
import { BundleSelector } from './BundleSelector';
import { ContentCard } from './ContentCard';
import { getVideosByTag } from '../../services/videos';
import { getAllMedia } from '../../services/media';
import { checkUserMembership, checkAllowUser } from '../../utils/checkSubscription';
import type {
  MujerSoberanaHubProps,
  ContentCard as IContentCard,
  Bundle,
  ViewType,
} from './types';

interface HubVideoCard extends IContentCard {
  tags: string[];
  seriesId: string;
  seriesLabel: string;
}

interface HubTag {
  id: string;
  label: string;
  active: boolean;
}

const ALL_TAG_ID = 'all';
const MAX_VISIBLE_TAGS = 5;
const ALL_SERIES_ID = 'all-series';
const DEFAULT_BUNDLE_IMAGE = 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400&q=80';
const STRIPE_CHECKOUT_BY_PLAN: Record<string, string> = {
  monthly: 'https://buy.stripe.com/6oU3cubp8duD5yw0aVffy0X',
  quarterly: 'https://buy.stripe.com/14A3cu64O76f9OMe1Lffy0W',
};

const getNormalizedAllowedSubscriptions = (video: any): string[] => {
  const rawAllowed =
    video?.allowedSubscriptions ??
    video?.allowedSubscription ??
    [];

  const list = Array.isArray(rawAllowed) ? rawAllowed : [rawAllowed];

  return list
    .map((value: unknown) => String(value || '').trim().toUpperCase())
    .filter(Boolean);
};

const formatTagLabel = (rawTag: string) => {
  const cleaned = rawTag
    .replace(/^#/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();

  if (!cleaned) return '#Sin etiqueta';

  const pretty = cleaned
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

  return `#${pretty}`;
};

const formatSeriesLabel = (rawSeries: string) => {
  const cleaned = rawSeries
    .replace(/^#/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();

  if (!cleaned) return 'Serie sin nombre';

  return cleaned
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const formatMinutesToDuration = (minutes: number) => {
  const total = Number.isFinite(minutes) ? Math.max(0, Math.round(minutes)) : 0;
  const hrs = Math.floor(total / 60);
  const mins = total % 60;
  if (hrs === 0) return `${mins}m`;
  return `${hrs}h ${mins}m`;
};

const formatSecondsToDuration = (seconds: number) => {
  const total = Number.isFinite(seconds) ? Math.max(0, Math.round(seconds)) : 0;
  const mins = Math.floor(total / 60);
  const secs = total % 60;

  if (mins <= 0) {
    return `${secs}s`;
  }

  return `${mins}m`;
};

/**
 * MujerSoberanaHub Component
 * Main platform component for the Mujer Soberana sanctuary
 * Combines sidebar filters, bundle selector, and content grids
 */
export const MujerSoberanaHub: React.FC<MujerSoberanaHubProps> = ({
  className = '',
  onNavigate,
  enableAnimations = true,
}) => {
  // Redux state
  const currentUser = useSelector((state: any) => state.user.currentUser);
  const navigate = useNavigate();

  // Modals & UI State
  const [isSubscriptionOpen, setIsSubscriptionOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewType>('v3');
  const [activeTagId, setActiveTagId] = useState<string>(ALL_TAG_ID);
  const [activeSeriesId, setActiveSeriesId] = useState<string>(ALL_SERIES_ID);
  const [showAllTags, setShowAllTags] = useState(false);
  const [savedCards, setSavedCards] = useState<Set<string>>(new Set());

  // Content State
  const [videoCards, setVideoCards] = useState<HubVideoCard[]>([]);
  const [audioCards, setAudioCards] = useState<IContentCard[]>([]);
  const [isLoadingVideos, setIsLoadingVideos] = useState(true);
  const [isLoadingMedia, setIsLoadingMedia] = useState(true);
  
  // Subscription state
  const [subscriptionUserList, setSubscriptionUserList] = useState<any[]>([]);
  const [subscriptionCheckCompleted, setSubscriptionCheckCompleted] = useState(false);

  // Check user subscription status
  useEffect(() => {
    const checkSubscription = async () => {
      setSubscriptionCheckCompleted(false);
      try {
        if (currentUser) {
          const subscriptionResult = await checkUserMembership(currentUser);
          setSubscriptionUserList(subscriptionResult);
        } else {
          setSubscriptionUserList([]);
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
      } finally {
        setSubscriptionCheckCompleted(true);
      }
    };

    checkSubscription();
  }, [currentUser]);

  // Fetch videos with Mujer_Soberana tag
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setIsLoadingVideos(true);
        const response = await getVideosByTag('Mujer_Soberana');
        const videos = Array.isArray(response?.data) ? response.data : [];
        let allowedList: boolean[] = [];
        
        // Check access for each video
        if (currentUser && videos.length > 0) {
          allowedList = await Promise.all(
            videos.map((video: any) => 
              checkAllowUser(video, currentUser, subscriptionUserList)
            )
          );
        }
        
        // Transform API response to ContentCard format
        const transformedVideos: HubVideoCard[] = videos.map((video: any, index: number) => {
          // Check if user is Sara (admin), has matching subscription, or video is FREE.
          const normalizedAllowedSubscriptions = getNormalizedAllowedSubscriptions(video);
          const hasFreeAccess = normalizedAllowedSubscriptions.includes('FREE');
          const isAllowed =
            currentUser?.email === 'sara.asanas@gmail.com' ||
            hasFreeAccess ||
            Boolean(allowedList[index]);
          const videoId =
            (typeof video?.videoId === 'string' && video.videoId.trim()) ||
            (typeof video?._id === 'string' && video._id.trim()) ||
            '';
          const tags = Array.isArray(video?.tags)
            ? video.tags.filter((tag: unknown) => typeof tag === 'string' && tag.trim() !== '')
            : [];
          const rawSeriesId =
            (typeof video?.seriesId === 'string' && video.seriesId.trim())
              ? video.seriesId
              : (typeof video?.series === 'string' && video.series.trim())
                ? video.series
                : 'sin-serie';
          const seriesId = rawSeriesId.trim();
          const seriesLabel = formatSeriesLabel(seriesId);
          
          return {
            id: videoId,
            title: video.title,
            description: video.description || '',
            category: video.category || tags[0] || video.series || 'Video',
            level: video.intensityLevel || 'moderate',
            format: 'video' as const,
            duration: `${video.duration} min`,
            thumbnail: video.thumbnail || DEFAULT_BUNDLE_IMAGE,
            tags,
            seriesId,
            seriesLabel,
            instructor: video.instructor ? {
              name: video.instructor,
              initials: video.instructor.split(' ').map((n: string) => n[0]).join('')
            } : undefined,
            isPremium: !isAllowed,
          };
        });
        
        setVideoCards(transformedVideos);
      } catch (error) {
        console.error('Error fetching Mujer Soberana videos:', error);
        setVideoCards([]);
      } finally {
        setIsLoadingVideos(false);
      }
    };

    if (!currentUser || subscriptionCheckCompleted) {
      fetchVideos();
    }
  }, [currentUser, subscriptionUserList, subscriptionCheckCompleted]);

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        setIsLoadingMedia(true);
        const response = await getAllMedia();
        const mediaItems = Array.isArray(response?.data) ? response.data : [];
        let allowedList: boolean[] = [];

        if (currentUser && mediaItems.length > 0) {
          allowedList = await Promise.all(
            mediaItems.map((item: any) => checkAllowUser(item, currentUser, subscriptionUserList))
          );
        }

        const transformedMedia: IContentCard[] = mediaItems
          .filter((item: any) => {
            const type = String(item?.mediaType || '').trim().toUpperCase();
            return type === 'AUDIO' || type === 'PDF';
          })
          .map((item: any, index: number) => {
            const normalizedAllowedSubscriptions = getNormalizedAllowedSubscriptions(item);
            const hasFreeAccess = normalizedAllowedSubscriptions.includes('FREE');
            const isAllowed =
              currentUser?.email === 'sara.asanas@gmail.com' ||
              hasFreeAccess ||
              Boolean(allowedList[index]);

            const mediaType = String(item?.mediaType || '').trim().toUpperCase();
            const format = mediaType === 'PDF' ? 'guide' : 'audio';
            const durationValue = Number(item?.duration || 0);
            const displayDuration =
              mediaType === 'PDF'
                ? (durationValue > 0 ? `${durationValue} paginas` : 'PDF')
                : formatSecondsToDuration(durationValue);

            return {
              id:
                (typeof item?.mediaId === 'string' && item.mediaId.trim()) ||
                (typeof item?._id === 'string' && item._id.trim()) ||
                `media-${index}`,
              title: item?.title || (mediaType === 'PDF' ? 'Guia PDF' : 'Audio'),
              description: item?.description || '',
              category: item?.category || (mediaType === 'PDF' ? 'Recurso' : 'Meditacion'),
              level: item?.intensityLevel || 'restorative',
              format,
              duration: displayDuration,
              thumbnail: item?.thumbnail || DEFAULT_BUNDLE_IMAGE,
              filePath: item?.filePath || item?.path || item?.mediaUrl || item?.url || '',
              isPremium: !isAllowed,
            } as IContentCard;
          });

        setAudioCards(transformedMedia);
      } catch (error) {
        console.error('Error fetching Mujer Soberana media:', error);
        setAudioCards([]);
      } finally {
        setIsLoadingMedia(false);
      }
    };

    if (!currentUser || subscriptionCheckCompleted) {
      fetchMedia();
    }
  }, [currentUser, subscriptionUserList, subscriptionCheckCompleted]);

  const availableTagIds = useMemo(() => {
    const uniqueTags = new Set<string>();
    videoCards.forEach((card) => {
      card.tags.forEach((tag) => {
        if (typeof tag === 'string' && tag.trim()) {
          uniqueTags.add(tag.trim());
        }
      });
    });
    return Array.from(uniqueTags).sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
  }, [videoCards]);

  const intentTags = useMemo<HubTag[]>(
    () => [
      { id: ALL_TAG_ID, label: '#Todas las practicas', active: activeTagId === ALL_TAG_ID },
      ...availableTagIds.map((tagId) => ({
        id: tagId,
        label: formatTagLabel(tagId),
        active: activeTagId === tagId,
      })),
    ],
    [availableTagIds, activeTagId]
  );

  const filteredVideoCards = useMemo(() => {
    return videoCards.filter((card) => {
      const matchesTag = activeTagId === ALL_TAG_ID || card.tags.includes(activeTagId);
      const matchesSeries = activeSeriesId === ALL_SERIES_ID || card.seriesId === activeSeriesId;
      return matchesTag && matchesSeries;
    });
  }, [videoCards, activeTagId, activeSeriesId]);

  const dynamicBundles = useMemo<Bundle[]>(() => {
    const grouped = new Map<string, HubVideoCard[]>();

    videoCards.forEach((card) => {
      const list = grouped.get(card.seriesId) || [];
      list.push(card);
      grouped.set(card.seriesId, list);
    });

    const seriesBundles = Array.from(grouped.entries())
      .map(([seriesId, cards]) => {
        const totalMinutes = cards.reduce((sum, card) => {
          const minutes = Number.parseInt(card.duration, 10);
          return sum + (Number.isNaN(minutes) ? 0 : minutes);
        }, 0);

        return {
          id: seriesId,
          title: cards[0]?.seriesLabel || formatSeriesLabel(seriesId),
          tag: 'Serie',
          imageUrl: cards[0]?.thumbnail || DEFAULT_BUNDLE_IMAGE,
          classCount: cards.length,
          totalDuration: formatMinutesToDuration(totalMinutes),
          instructor: cards[0]?.instructor,
          isPremium: cards.every((card) => Boolean(card.isPremium)),
        };
      })
      .sort((a, b) => b.classCount - a.classCount);

    const allMinutes = videoCards.reduce((sum, card) => {
      const minutes = Number.parseInt(card.duration, 10);
      return sum + (Number.isNaN(minutes) ? 0 : minutes);
    }, 0);

    return [
      {
        id: ALL_SERIES_ID,
        title: 'Todas las series',
        tag: 'Vista general',
        imageUrl: videoCards[0]?.thumbnail || DEFAULT_BUNDLE_IMAGE,
        classCount: videoCards.length,
        totalDuration: formatMinutesToDuration(allMinutes),
      },
      ...seriesBundles,
    ];
  }, [videoCards]);

  const primaryIntentTags = useMemo(() => intentTags.slice(0, MAX_VISIBLE_TAGS), [intentTags]);
  const extraIntentTags = useMemo(() => intentTags.slice(MAX_VISIBLE_TAGS), [intentTags]);
  const shouldShowTagToggle = extraIntentTags.length > 0;

  const activeTagMeta = useMemo(
    () => intentTags.find((tag) => tag.id === activeTagId),
    [intentTags, activeTagId]
  );

  const isActiveTagHidden = useMemo(() => {
    if (showAllTags || !activeTagMeta) return false;
    return !primaryIntentTags.some((tag) => tag.id === activeTagMeta.id);
  }, [showAllTags, activeTagMeta, primaryIntentTags]);

  useEffect(() => {
    if (activeTagId !== ALL_TAG_ID && !availableTagIds.includes(activeTagId)) {
      setActiveTagId(ALL_TAG_ID);
    }
  }, [activeTagId, availableTagIds]);

  useEffect(() => {
    if (!dynamicBundles.some((bundle) => bundle.id === activeSeriesId)) {
      setActiveSeriesId(ALL_SERIES_ID);
    }
  }, [dynamicBundles, activeSeriesId]);

  // Handlers
  const handleTagChange = useCallback((tagId: string) => {
    setActiveTagId(tagId);
    onNavigate?.(`tag:${tagId}`);
  }, [onNavigate]);

  const handleViewChange = useCallback((newView: ViewType) => {
    setViewMode(newView);
  }, []);

  const handleBundleSelect = useCallback((bundle: Bundle) => {
    setActiveSeriesId(bundle.id);
    onNavigate?.(`series:${bundle.id}`);
  }, [onNavigate]);

  const handleSaveCard = useCallback((cardId: string) => {
    setSavedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(cardId)) {
        newSet.delete(cardId);
      } else {
        newSet.add(cardId);
      }
      return newSet;
    });
  }, []);

  const handleCardClick = useCallback((card: IContentCard) => {
    // If content is premium and user doesn't have access, open subscription modal
    if (card.isPremium) {
      setIsSubscriptionOpen(true);
    } else if (card.format === 'guide') {
      const pdfUrl = (card as any)?.filePath;
      if (typeof pdfUrl === 'string' && pdfUrl.trim()) {
        window.open(pdfUrl, '_blank', 'noopener,noreferrer');
      }
    } else {
      // Route to video player for accessible content.
      navigate(`/video/${card.id}`);
      onNavigate?.(`video:${card.id}`);
    }
  }, [navigate, onNavigate]);

  const handleSubscribe = async (planId: string) => {
    const stripeCheckoutUrl = STRIPE_CHECKOUT_BY_PLAN[String(planId || '').trim().toLowerCase()];

    if (stripeCheckoutUrl) {
      window.open(stripeCheckoutUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    onNavigate?.(`subscribe:${planId}`);
  };

  // Grid layout classes
  const getGridClass = () => {
    const baseClass = 'grid gap-6 mb-12';
    switch (viewMode) {
      case 'v2':
        return `${baseClass} grid-cols-1 md:grid-cols-2`;
      case 'v4':
        return `${baseClass} grid-cols-2 md:grid-cols-3 lg:grid-cols-4`;
      case 'vlist':
        return `${baseClass} grid-cols-1`;
      default: // v3
        return `${baseClass} grid-cols-1 md:grid-cols-2 lg:grid-cols-3`;
    }
  };

  return (
    <div className={`min-h-screen h-[100vh] overflow-auto bg-gradient-to-br from-sc-c2/20 to-white ${className}`}>
      {/* Main Content */}
      <main className="px-4 py-6 md:px-8 md:py-10 lg:px-12 lg:py-12 max-w-7xl mx-auto">
        {/* Hub Header */}
        <section className="mb-8 animate-fade-in">
          <div className="text-xs font-semibold text-sb-600 uppercase tracking-widest flex items-center gap-3 mb-3">
            <div className="w-6 h-0.5 bg-sb-600 rounded" />
            Santuario de Mujer Soberana
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-light leading-tight mb-2">
            Tu <em className="italic text-so-600">Santuario</em> Diario
          </h1>
          <p className="text-sm md:text-base font-light text-gray-600 tracking-wide mb-6">
            Practicas curadas para tu cuerpo, tu respiracion y tu proceso.
          </p>

          {/* Intent Tags */}
          <div className="space-y-2 md:space-y-3">
            <div className="flex flex-wrap gap-2">
              {primaryIntentTags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => handleTagChange(tag.id)}
                  className={`px-3 md:px-4 py-2 rounded-full text-[11px] md:text-xs font-medium uppercase tracking-wide transition-all touch-action-manipulation ${
                    tag.active
                      ? tag.id === ALL_TAG_ID
                        ? 'bg-so-600 text-white shadow-lg'
                        : 'bg-sb-600/15 text-sb-700 border border-sb-300'
                      : 'bg-white/85 text-gray-600 border border-so-100 backdrop-blur-sm hover:bg-so-600 hover:text-white active:bg-so-600 active:text-white'
                  }`}
                >
                  {tag.label}
                </button>
              ))}

              {shouldShowTagToggle && (
                <button
                  onClick={() => setShowAllTags((prev) => !prev)}
                  className="px-3 md:px-4 py-2 rounded-full text-[11px] md:text-xs font-semibold uppercase tracking-wide transition-all touch-action-manipulation bg-white text-sb-700 border border-sb-300 hover:bg-sb-600 hover:text-white"
                >
                  {showAllTags ? 'Ver menos' : 'Ver todas'}
                </button>
              )}
            </div>

            {isActiveTagHidden && activeTagMeta && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] md:text-xs text-gray-500 uppercase tracking-wider">Filtro activo:</span>
                <button
                  onClick={() => handleTagChange(activeTagMeta.id)}
                  className="px-3 py-1.5 rounded-full text-[11px] md:text-xs font-medium uppercase tracking-wide bg-sb-600/15 text-sb-700 border border-sb-300"
                >
                  {activeTagMeta.label}
                </button>
              </div>
            )}

            {showAllTags && shouldShowTagToggle && (
              <div className="rounded-2xl border border-so-100 bg-white/70 p-2.5 md:p-3">
                <div className="flex flex-wrap gap-2">
                  {extraIntentTags.map((tag) => (
                    <button
                      key={tag.id}
                      onClick={() => handleTagChange(tag.id)}
                      className={`px-3 md:px-4 py-2 rounded-full text-[11px] md:text-xs font-medium uppercase tracking-wide transition-all touch-action-manipulation ${
                        tag.active
                          ? 'bg-sb-600/15 text-sb-700 border border-sb-300'
                          : 'bg-white text-gray-600 border border-so-100 hover:bg-so-600 hover:text-white active:bg-so-600 active:text-white'
                      }`}
                    >
                      {tag.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Bundle Selector */}
        <BundleSelector bundles={dynamicBundles} onBundleSelect={handleBundleSelect} />

        {/* Controls Bar */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4 py-2.5 px-3.5 rounded-xl border border-so-100" style={{ background: 'rgba(234,229,200,0.45)' }}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 w-full md:w-auto">
            <span className="text-sm text-gray-600">
              Mostrando <strong>{filteredVideoCards.length + audioCards.length}</strong> practicas
            </span>
            <select className="bg-white/70 md:bg-transparent text-sm text-gray-900 font-light outline-none cursor-pointer rounded-lg border border-so-100 px-2.5 py-1.5 md:px-0 md:py-0 md:border-0 w-full sm:w-auto">
              <option>Ordenar: Recomendadas</option>
              <option>Ordenar: Mas recientes</option>
              <option>Ordenar: Menor duracion</option>
              <option>Ordenar: Mas populares</option>
            </select>
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-1">
            {/* Hide 4-col and list views on mobile */}
            {(['v2', 'v3', 'v4', 'vlist'] as const).map((view) => (
              <button
                key={view}
                onClick={() => handleViewChange(view)}
                className={`w-8 h-8 md:w-9 md:h-9 rounded-xl flex items-center justify-center transition-all touch-action-manipulation ${
                  viewMode === view
                    ? 'bg-so-600 text-white shadow-lg'
                    : 'text-gray-400 hover:bg-sb-100 hover:text-sb-600 active:bg-so-600 active:text-white'
                } ${
                  (view === 'v4' || view === 'vlist') ? 'hidden md:flex' : ''
                }`}
                title={`View: ${view}`}
                aria-label={`View mode: ${view}`}
              >
                  {view === 'v2' && (
                    <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor">
                      <rect x="1" y="1" width="6.5" height="6.5" rx="1.5" />
                      <rect x="8.5" y="1" width="6.5" height="6.5" rx="1.5" />
                      <rect x="1" y="8.5" width="6.5" height="6.5" rx="1.5" />
                      <rect x="8.5" y="8.5" width="6.5" height="6.5" rx="1.5" />
                    </svg>
                  )}
                  {view === 'v3' && (
                    <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor">
                      <rect x="1" y="1" width="4" height="6" rx="1" />
                      <rect x="6" y="1" width="4" height="6" rx="1" />
                      <rect x="11" y="1" width="4" height="6" rx="1" />
                      <rect x="1" y="9" width="4" height="6" rx="1" />
                      <rect x="6" y="9" width="4" height="6" rx="1" />
                      <rect x="11" y="9" width="4" height="6" rx="1" />
                    </svg>
                  )}
                  {view === 'v4' && (
                    <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor">
                      <rect x="1" y="1" width="2.8" height="5" rx="0.7" />
                      <rect x="4.7" y="1" width="2.8" height="5" rx="0.7" />
                      <rect x="8.4" y="1" width="2.8" height="5" rx="0.7" />
                      <rect x="12.1" y="1" width="2.8" height="5" rx="0.7" />
                      <rect x="1" y="8" width="2.8" height="5" rx="0.7" />
                      <rect x="4.7" y="8" width="2.8" height="5" rx="0.7" />
                      <rect x="8.4" y="8" width="2.8" height="5" rx="0.7" />
                      <rect x="12.1" y="8" width="2.8" height="5" rx="0.7" />
                    </svg>
                  )}
                  {view === 'vlist' && (
                    <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor">
                      <rect x="1" y="1.5" width="14" height="3.5" rx="1" />
                      <rect x="1" y="6.3" width="14" height="3.5" rx="1" />
                      <rect x="1" y="11" width="14" height="3.5" rx="1" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>

        {/* Video Section */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-5 h-px bg-gray-300" />
              <h2 className="text-xs font-semibold tracking-widest text-gray-600 uppercase">
                Clases en Video
              </h2>
            </div>
            <a
              href="#"
              className="text-xs font-semibold text-sb-600 uppercase tracking-widest hover:border-b border-sb-600 transition-colors"
            >
              Ver Todo →
            </a>
          </div>

          {isLoadingVideos ? (
            <div className={getGridClass()}>
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-200 rounded-2xl aspect-video mb-3" />
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : filteredVideoCards.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No se encontraron videos para el tag seleccionado</p>
            </div>
          ) : (
            <div className={getGridClass()}>
              {filteredVideoCards.map((card) => (
                <ContentCard
                  key={card.id}
                  card={{ ...card, isSaved: savedCards.has(card.id) }}
                  viewMode={viewMode}
                  onCardClick={handleCardClick}
                  onSave={handleSaveCard}
                />
              ))}
            </div>
          )}
        </section>

        {/* Divider */}
        <div className="flex items-center gap-4 my-12">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-sb-300 to-transparent" />
          <span className="text-2xl text-so-300">✦</span>
          <div className="flex-1 h-px bg-gradient-to-l from-transparent via-sb-300 to-transparent" />
        </div>

        {/* Audio Section */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-5 h-px bg-gray-300" />
              <h2 className="text-xs font-semibold tracking-widest text-gray-600 uppercase">
                Meditaciones Guiadas y Recursos
              </h2>
            </div>
            <a
              href="#"
              className="text-xs font-semibold text-sb-600 uppercase tracking-widest hover:border-b border-sb-600 transition-colors"
            >
              Ver Todo →
            </a>
          </div>

          {isLoadingMedia ? (
            <div className={getGridClass()}>
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-200 rounded-2xl aspect-video mb-3" />
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : audioCards.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No se encontraron audios o guias PDF disponibles</p>
            </div>
          ) : (
            <div className={getGridClass()}>
              {audioCards.map((card) => (
                <ContentCard
                  key={card.id}
                  card={{ ...card, isSaved: savedCards.has(card.id) }}
                  onCardClick={handleCardClick}
                  viewMode={viewMode}
                  onSave={handleSaveCard}
                />
              ))}
            </div>
          )}
        </section>

        {/* JoinBanner */}
        <section className="relative bg-gradient-to-r from-sb-800 via-sb-700 to-sb-600 rounded-2xl md:rounded-3xl p-6 md:p-8 lg:p-12 overflow-hidden shadow-2xl">
          {/* Ambient Light */}
          <div className="absolute inset-0 bg-radial-gradient from-so-300/10 via-transparent to-transparent pointer-events-none" />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-9 relative z-10">
            {/* Content */}
            <div>
              <div className="text-so-300 text-xs font-semibold tracking-widest uppercase mb-3">
                ✦ Membresia Mujer Soberana
              </div>
              <h2 className="text-2xl md:text-3xl font-light text-white leading-tight mb-3">
                Desbloquea tu <br />
                <em className="italic text-so-300">Biblioteca Completa</em>
              </h2>
              <p className="text-white/65 text-sm font-light leading-relaxed max-w-md mb-6">
                200+ clases en video, meditaciones guiadas, sesiones de respiracion y recursos
                exclusivos para mujeres que estan regresando a si mismas.
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col lg:items-end lg:justify-center gap-4 lg:gap-6">
              <button
                onClick={() => setIsSubscriptionOpen(true)}
                className="w-full lg:w-auto px-8 py-4 bg-gradient-to-r from-so-600 to-so-300 text-white font-semibold uppercase tracking-widest rounded-full text-sm hover:shadow-2xl hover:-translate-y-1 transition-all shadow-lg touch-action-manipulation"
              >
                Unirme
              </button>

              {/* Features */}
              <div className="grid grid-cols-2 lg:flex lg:flex-col gap-2 lg:text-right">
                <div className="flex items-center lg:justify-end gap-2 text-white/55 text-xs font-light">
                  <span className="w-1.5 h-1.5 rounded-full bg-so-300 flex-shrink-0" />
                  Acceso ilimitado
                </div>
                <div className="flex items-center lg:justify-end gap-2 text-white/55 text-xs font-light">
                  <span className="w-1.5 h-1.5 rounded-full bg-so-300 flex-shrink-0" />
                  Nuevas clases cada semana
                </div>
                <div className="flex items-center lg:justify-end gap-2 text-white/55 text-xs font-light">
                  <span className="w-1.5 h-1.5 rounded-full bg-so-300 flex-shrink-0" />
                  Ceremonias en vivo
                </div>
                <div className="flex items-center lg:justify-end gap-2 text-white/55 text-xs font-light">
                  <span className="w-1.5 h-1.5 rounded-full bg-so-300 flex-shrink-0" />
                  Guias en PDF
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={isSubscriptionOpen}
        onClose={() => setIsSubscriptionOpen(false)}
        onSubscribe={handleSubscribe}
      />
    </div>
  );
};

export default MujerSoberanaHub;
